import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link as ReactLink } from "react-router-dom";
import { useTracking } from "react-tracking";

import { AirplaneMultipleTrips } from "@airhelp/icons";
import { Box, Button, Flex, HStack } from "@chakra-ui/react";

import { getEssentialProduct } from "components/Membership/membershipHelper";
import { PAGE_PATHS } from "config/routes";
import { Loader } from "elements";
import useFetchOffers from "hooks/api/offers/useFetchOffers";
import useEffectOnce from "hooks/useEffectOnce";
import { Offer } from "models/Offers";
import { usePreferencesStore } from "stores/preferences";
import { formatValueToCurrency, getLocalPriceValue } from "stores/preferences/currencies";
import { useTrackEvent } from "utils/tracking/hooks";

const UpsellEssential = () => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const { isLoading: isLoadingOffers, data } = useFetchOffers();
  const { trackUpsellOption } = useTrackEvent();
  const currency = usePreferencesStore((state) => state.currency);

  const handleUpsellEssentialClick = () => {
    trackEvent({
      name: "GAEvent",
      eventCategory: "ahplus",
      eventAction: "clicked",
      eventLabel: "ahplus_dashboard_home_upsell_essential_clicked",
    });
    trackUpsellOption("get membership essential", "home page");
  };

  useEffectOnce(() => {
    trackEvent({
      name: "GAEvent",
      eventCategory: "ahplus",
      eventAction: "viewed",
      eventLabel: "ahplus_dashboard_home_upsell_essential_shown",
    });
  });

  if (isLoadingOffers) {
    return <Loader dataTestId="loader" />;
  }

  const offers = data?.offers as Record<string, Offer>;

  const product = getEssentialProduct(offers);
  const localPrice = getLocalPriceValue(product?.prices, currency);

  return (
    <Flex
      px={4}
      py={3}
      background="white"
      borderRadius="xl"
      justifyContent="space-between"
      alignItems={{ base: "flex-start", md: "center" }}
      flexDirection={{ base: "column", md: "row" }}
    >
      {/* icon and copy part */}
      <HStack alignItems="center" spacing={4}>
        <Box p={4} background="primary.100" border="1px transparent" borderRadius="md">
          <AirplaneMultipleTrips color="primary.500" h={6} w="auto" />
        </Box>

        <Box fontWeight="medium" fontSize="md">
          {t("trips.upgrade.annual.annual_title")}
        </Box>
      </HStack>

      {/* button part */}
      <Box width={{ base: "100%", md: "auto" }} mt={{ base: 4, md: 0 }}>
        <Button
          as={ReactLink}
          onClick={handleUpsellEssentialClick}
          to={{ pathname: generatePath(PAGE_PATHS.MEMBERSHIPS.BUY_PAGE), search: "channel=dashboard_home" }}
          size="xs"
          width="100%"
          data-testid="upsell-essential"
        >
          {t("trips.upgrade.annual.button_buy_essential_annual", {
            value: formatValueToCurrency(localPrice, currency),
          })}
        </Button>
      </Box>
    </Flex>
  );
};

export default UpsellEssential;
